import { Component, OnInit, OnDestroy } from '@angular/core';
import { MessageService, ConfirmationService } from 'primeng/api';
import { AlertService } from '@app/_services';
import { PropertyService } from '@app/_components/yield-max/property/property.service';
import { ReservationsService } from '@app/_components/yield-max/reservation/reservation.service';
import { OpsDashboardService } from '@app/_components/yield-max/ops-dashboard/ops-dashboard.service';
import { forkJoin, interval, Subscription } from 'rxjs';
import { switchMap, takeWhile } from 'rxjs/operators';
import { RateRegenService, RateRegenRequest } from './rate-regen.service';

@Component({
    selector: 'app-yieldmax-admin',
    templateUrl: './yieldmax-admin.component.html',
    styleUrls: ['./yieldmax-admin.component.css'],
    providers: [MessageService, ConfirmationService],
    standalone: false
})
export class YieldmaxAdminComponent implements OnInit, OnDestroy {

    // Property multi-selector
    properties: any[] = [];
    selectedProperties: any[] = [];

    // Loading flags — one per action
    loadingSyncAllProperties    = false;
    loadingSyncAllReservations  = false;
    loadingGenerateAllRates     = false;
    loadingSurgicalRemoveAll    = false;

    loadingSyncProperty         = false;
    loadingSyncReservations     = false;
    loadingGenerateRates        = false;
    loadingRemoveRegenerateAll  = false;
    loadingSurgicalRemove       = false;

    // ─── Statistics (last 24h, aggregate) ──────────────────────────────────────
    statsRows: any[] = [];
    statsLoading  = false;
    statsLastRefresh: Date = null;

    // ─── Per-property sync table (last 24h) ─────────────────────────────────────
    propertyRows: any[] = [];
    propertyRowsFilter = '';
    /** today's date (yyyy-MM-dd) passed to the logman list as the day filter when a count is clicked */
    todayStr = '';

    // ─── Health checks ────────────────────────────────────────────────────────
    healthSummary: any = null;
    healthLoading = false;
    healthLastRefresh: Date = null;
    drillDownVisible   = false;
    drillDownTitle     = '';
    drillDownDesc      = '';
    drillDownProperties: any[] = [];

    // SQS dashboard
    sqsQueues: any[]         = [];
    loadingSqs               = false;
    sqsLastRefresh: Date | null = null;

    // ─── Task #86 — Box-targeted regen ───────────────────────────────────────────
    // ATL box state
    atlAllLoading      = false;
    atlRangeLoading    = false;
    atlRangeStart: number | null = null;
    atlRangeEnd: number | null   = null;
    atlActiveJob: { id: number; box: 'ATL'; log?: any } | null = null;
    atlPollSub: Subscription | null = null;

    // NYC box state
    nycAllLoading      = false;
    nycRangeLoading    = false;
    nycRangeStart: number | null = null;
    nycRangeEnd: number | null   = null;
    nycActiveJob: { id: number; box: 'NYC'; log?: any } | null = null;
    nycPollSub: Subscription | null = null;

    // ─── Task #95 — Job history ───────────────────────────────────────────────────
    jobHistory: Array<{ id: number; box: 'ATL' | 'NYC'; scope: string; log?: any }> = [];
    jobHistoryRefreshSub: Subscription | null = null;

    constructor(
        private propertyService: PropertyService,
        private reservationService: ReservationsService,
        private opsDashboardService: OpsDashboardService,
        private alertService: AlertService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private rateRegenService: RateRegenService
    ) {}

    ngOnInit(): void {
        this.loadProperties();
        this.loadSqsStatus();
        this.loadJobHistory();
        this.loadStats();
        this.loadHealth();
        const d = new Date();
        const pad = (n: number) => (n < 10 ? '0' + n : '' + n);
        this.todayStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    }

    ngOnDestroy(): void {
        this.atlPollSub?.unsubscribe();
        this.nycPollSub?.unsubscribe();
        this.jobHistoryRefreshSub?.unsubscribe();
    }

    // ─── SQS Queue Status ────────────────────────────────────────────────────

    loadSqsStatus(): void {
        this.loadingSqs = true;
        this.propertyService.getSqsStatus().subscribe({
            next: (res: any) => {
                this.sqsQueues = res?.data?.sqsQueues ?? [];
                this.sqsLastRefresh = new Date();
            },
            error: (err) => {
                this.messageService.add({ severity: 'warn', summary: 'SQS Status', detail: 'Could not load SQS queue status.' });
            },
            complete: () => { this.loadingSqs = false; }
        });
    }

    // ─── Job history (loaded on init + refreshable) ───────────────────────────

    loadJobHistory(): void {
        // Fire both RPT boxes in parallel; merge results sorted newest-first
        this.rateRegenService.getRecentJobsAtl(20).subscribe({
            next: (res: any) => {
                const jobs: any[] = res?.data?.jobs ?? [];
                jobs.forEach(j => {
                    if (!this.jobHistory.find(h => h.id === j.scheduleJobLogId)) {
                        this.jobHistory.push({ id: j.scheduleJobLogId, box: 'ATL', scope: j.metadata ?? j.triggerName ?? '—', log: j });
                    }
                });
                this.jobHistory.sort((a, b) => b.id - a.id);
                if (this.jobHistory.length > 50) { this.jobHistory.length = 50; }
            },
            error: () => { /* ATL-RPT unavailable — silently skip */ }
        });
        this.rateRegenService.getRecentJobsNyc(20).subscribe({
            next: (res: any) => {
                const jobs: any[] = res?.data?.jobs ?? [];
                jobs.forEach(j => {
                    if (!this.jobHistory.find(h => h.id === j.scheduleJobLogId)) {
                        this.jobHistory.push({ id: j.scheduleJobLogId, box: 'NYC', scope: j.metadata ?? j.triggerName ?? '—', log: j });
                    }
                });
                this.jobHistory.sort((a, b) => b.id - a.id);
                if (this.jobHistory.length > 50) { this.jobHistory.length = 50; }
            },
            error: () => { /* NYC-RPT unavailable — silently skip */ }
        });
    }

    // ─── Property list for multi-select ──────────────────────────────────────

    loadProperties(): void {
        this.propertyService.propertySummaryList().subscribe({
            next: (res: any) => {
                this.properties = (res?.data?.propertySummaryList ?? []).map((p: any) => ({
                    ...p,
                    label: p.propertyCode ?? p.propertyId
                }));
            },
            error: () => {
                this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Could not load property list.' });
            }
        });
    }

    get hasSelection(): boolean {
        return this.selectedProperties?.length > 0;
    }

    private selectionLabel(): string {
        const n = this.selectedProperties.length;
        return n === 1 ? this.selectedProperties[0].label : `${n} properties`;
    }

    formatAvgTime(ms: number | null | undefined): string {
        if (ms == null) { return '—'; }
        if (ms < 1000)  { return `${ms}ms`; }
        if (ms < 60000) { return `${(ms / 1000).toFixed(1)}s`; }
        const m = Math.floor(ms / 60000);
        const s = Math.round((ms % 60000) / 1000);
        return `${m}m ${s}s`;
    }

    // ─── ALL PROPERTIES actions ───────────────────────────────────────────────

    confirmSyncAllProperties(): void {
        this.confirmationService.confirm({
            message: 'Sync property data from Wiki into YieldMax for ALL properties?',
            header: 'Confirm Sync All Properties',
            icon: 'pi pi-refresh',
            accept: () => this.syncAllProperties(),
            reject: () => this.messageService.add({ severity: 'info', summary: 'Cancelled', detail: 'Operation cancelled.' })
        });
    }

    syncAllProperties(): void {
        this.loadingSyncAllProperties = true;
        this.propertyService.syncAllProperties().subscribe({
            next: (res: any) => {
                this.messageService.add({ severity: 'success', summary: 'Sync All Properties', detail: res?.message ?? 'Completed.' });
            },
            error: (err) => {
                this.messageService.add({ severity: 'error', summary: 'Sync All Properties', detail: 'Failed: ' + (err?.error?.message ?? err?.message ?? 'Unknown error') });
            },
            complete: () => { this.loadingSyncAllProperties = false; }
        });
    }

    confirmSyncAllReservations(): void {
        this.confirmationService.confirm({
            message: 'Sync reservation data from Wiki into YieldMax for ALL properties?',
            header: 'Confirm Sync All Reservations',
            icon: 'pi pi-refresh',
            accept: () => this.syncAllReservations(),
            reject: () => this.messageService.add({ severity: 'info', summary: 'Cancelled', detail: 'Operation cancelled.' })
        });
    }

    syncAllReservations(): void {
        this.loadingSyncAllReservations = true;
        this.reservationService.syncAllReservations().subscribe({
            next: (res: any) => {
                this.messageService.add({ severity: 'success', summary: 'Sync All Reservations', detail: res?.message ?? 'Completed.' });
            },
            error: (err) => {
                this.messageService.add({ severity: 'error', summary: 'Sync All Reservations', detail: 'Failed: ' + (err?.error?.message ?? err?.message ?? 'Unknown error') });
            },
            complete: () => { this.loadingSyncAllReservations = false; }
        });
    }

    confirmGenerateAllRates(): void {
        this.confirmationService.confirm({
            message: 'Recalculate and push rates and LOS pricing for ALL active and suspended properties? This may take several minutes.',
            header: 'Confirm Generate All Rates & LOS',
            icon: 'pi pi-cog',
            accept: () => this.generateAllRates(),
            reject: () => this.messageService.add({ severity: 'info', summary: 'Cancelled', detail: 'Operation cancelled.' })
        });
    }

    generateAllRates(): void {
        this.loadingGenerateAllRates = true;
        this.propertyService.generateAllRates().subscribe({
            next: (res: any) => {
                this.messageService.add({ severity: 'success', summary: 'Generate All Rates & LOS', detail: res?.message ?? 'Completed.' });
            },
            error: (err) => {
                this.messageService.add({ severity: 'error', summary: 'Generate All Rates & LOS', detail: 'Failed: ' + (err?.error?.message ?? err?.message ?? 'Unknown error') });
            },
            complete: () => { this.loadingGenerateAllRates = false; }
        });
    }

    confirmSurgicalRemoveAll(): void {
        this.confirmationService.confirm({
            message: 'Remove stale LOS records for ALL properties? (records older than 2 days, inactive, or below min LOS)',
            header: 'Confirm Surgical Remove — All Properties',
            icon: 'pi pi-exclamation-triangle',
            accept: () => this.surgicalRemoveAllLos(),
            reject: () => this.messageService.add({ severity: 'info', summary: 'Cancelled', detail: 'Operation cancelled.' })
        });
    }

    surgicalRemoveAllLos(): void {
        this.loadingSurgicalRemoveAll = true;
        this.propertyService.surgicalRemoveAllLos().subscribe({
            next: (res: any) => {
                this.messageService.add({ severity: 'success', summary: 'Surgical Remove LOS — All', detail: res?.message ?? 'Completed.' });
            },
            error: (err) => {
                this.messageService.add({ severity: 'error', summary: 'Surgical Remove LOS — All', detail: 'Failed: ' + (err?.error?.message ?? err?.message ?? 'Unknown error') });
            },
            complete: () => { this.loadingSurgicalRemoveAll = false; }
        });
    }

    // ─── BY PROPERTY actions (run in parallel across all selected) ────────────

    confirmSyncProperty(): void {
        this.confirmationService.confirm({
            message: `Sync property data from Wiki into YieldMax for ${this.selectionLabel()}?`,
            header: 'Confirm Sync Property',
            icon: 'pi pi-refresh',
            accept: () => this.syncProperty(),
            reject: () => this.messageService.add({ severity: 'info', summary: 'Cancelled', detail: 'Operation cancelled.' })
        });
    }

    syncProperty(): void {
        if (!this.hasSelection) { return; }
        this.loadingSyncProperty = true;
        forkJoin(this.selectedProperties.map(p => this.propertyService.syncPropertyById(p.propertyId))).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Sync Property', detail: `Completed for ${this.selectionLabel()}.` });
            },
            error: (err) => {
                this.messageService.add({ severity: 'error', summary: 'Sync Property', detail: 'Failed: ' + (err?.error?.message ?? err?.message ?? 'Unknown error') });
            },
            complete: () => { this.loadingSyncProperty = false; }
        });
    }

    confirmSyncReservations(): void {
        this.confirmationService.confirm({
            message: `Sync reservations from Wiki into YieldMax for ${this.selectionLabel()}?`,
            header: 'Confirm Sync Reservations',
            icon: 'pi pi-refresh',
            accept: () => this.syncReservations(),
            reject: () => this.messageService.add({ severity: 'info', summary: 'Cancelled', detail: 'Operation cancelled.' })
        });
    }

    syncReservations(): void {
        if (!this.hasSelection) { return; }
        this.loadingSyncReservations = true;
        forkJoin(this.selectedProperties.map(p => this.propertyService.syncReservationsByProperty(p.propertyId))).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Sync Reservations', detail: `Completed for ${this.selectionLabel()}.` });
            },
            error: (err) => {
                this.messageService.add({ severity: 'error', summary: 'Sync Reservations', detail: 'Failed: ' + (err?.error?.message ?? err?.message ?? 'Unknown error') });
            },
            complete: () => { this.loadingSyncReservations = false; }
        });
    }

    confirmGenerateRates(): void {
        this.confirmationService.confirm({
            message: `Recalculate rates and LOS pricing for ${this.selectionLabel()} and push to Wiki?`,
            header: 'Confirm Generate Rates & LOS',
            icon: 'pi pi-cog',
            accept: () => this.generateRates(),
            reject: () => this.messageService.add({ severity: 'info', summary: 'Cancelled', detail: 'Operation cancelled.' })
        });
    }

    generateRates(): void {
        if (!this.hasSelection) { return; }
        this.loadingGenerateRates = true;
        forkJoin(this.selectedProperties.map(p => this.propertyService.generateRateByProperty(p.propertyId))).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Generate Rates & LOS', detail: `Completed for ${this.selectionLabel()}.` });
            },
            error: (err) => {
                this.messageService.add({ severity: 'error', summary: 'Generate Rates & LOS', detail: 'Failed: ' + (err?.error?.message ?? err?.message ?? 'Unknown error') });
            },
            complete: () => { this.loadingGenerateRates = false; }
        });
    }

    confirmRemoveRegenerateAll(): void {
        this.confirmationService.confirm({
            message: `Delete ALL LOS data for ${this.selectionLabel()} and immediately regenerate? This cannot be undone.`,
            header: 'Confirm Remove & Regenerate',
            icon: 'pi pi-exclamation-triangle',
            accept: () => this.removeAndRegenerateAllLos(),
            reject: () => this.messageService.add({ severity: 'info', summary: 'Cancelled', detail: 'Operation cancelled.' })
        });
    }

    removeAndRegenerateAllLos(): void {
        if (!this.hasSelection) { return; }
        this.loadingRemoveRegenerateAll = true;
        forkJoin(this.selectedProperties.map(p => this.propertyService.yieldMaxDeleteAndRegenerateAllLos(p.propertyId))).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Remove & Regenerate LOS', detail: `Completed for ${this.selectionLabel()}.` });
            },
            error: (err) => {
                this.messageService.add({ severity: 'error', summary: 'Remove & Regenerate LOS', detail: 'Failed: ' + (err?.error?.message ?? err?.message ?? 'Unknown error') });
            },
            complete: () => { this.loadingRemoveRegenerateAll = false; }
        });
    }

    confirmSurgicalRemoveByProperty(): void {
        this.confirmationService.confirm({
            message: `Surgically remove stale LOS records for ${this.selectionLabel()}?`,
            header: 'Confirm Surgical Remove',
            icon: 'pi pi-exclamation-triangle',
            accept: () => this.surgicalRemoveLos(),
            reject: () => this.messageService.add({ severity: 'info', summary: 'Cancelled', detail: 'Operation cancelled.' })
        });
    }

    surgicalRemoveLos(): void {
        if (!this.hasSelection) { return; }
        this.loadingSurgicalRemove = true;
        forkJoin(this.selectedProperties.map(p => this.propertyService.yieldMaxSurgicalDeleteRateLos(p.propertyId))).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Surgical Remove LOS', detail: `Completed for ${this.selectionLabel()}.` });
            },
            error: (err) => {
                this.messageService.add({ severity: 'error', summary: 'Surgical Remove LOS', detail: 'Failed: ' + (err?.error?.message ?? err?.message ?? 'Unknown error') });
            },
            complete: () => { this.loadingSurgicalRemove = false; }
        });
    }

    // ─── Statistics + Per-property sync table (last 24h) ────────────────────────

    /**
     * Single call to /property-sync-24h drives BOTH the Process Statistics summary
     * (aggregate, 4 rows) AND the per-property table at the bottom. Everything is a
     * rolling last-24-hours window sourced from str_logman (manual + scheduled syncs).
     * If exactly one property is selected in the multiselect, the data is scoped to it.
     */
    loadStats(): void {
        this.statsLoading = true;
        const req = (this.selectedProperties?.length === 1)
            ? { propertyId: this.selectedProperties[0].propertyId }
            : {};

        this.opsDashboardService.getPropertySync24h(req).subscribe({
            next: (res: any) => {
                const d = res?.data?.propertySync;
                this.statsRows = [
                    { label: 'Properties',   icon: 'pi-globe',    synced: d?.totalPropertySync    ?? 0, errors: d?.totalPropertyErr    ?? 0 },
                    { label: 'Reservations', icon: 'pi-calendar', synced: d?.totalReservationSync ?? 0, errors: d?.totalReservationErr ?? 0 },
                    { label: 'Rates',        icon: 'pi-dollar',   synced: d?.totalRateSync        ?? 0, errors: d?.totalRateErr        ?? 0 },
                    { label: 'Rate LOS',     icon: 'pi-th-large', synced: d?.totalRateLosSync     ?? 0, errors: d?.totalRateLosErr     ?? 0 },
                ];
                this.propertyRows = d?.properties ?? [];
                this.statsLastRefresh = new Date();
            },
            error: () => {
                this.messageService.add({ severity: 'error', summary: 'Statistics', detail: 'Failed to load 24h sync statistics.' });
                this.statsLoading = false;
            },
            complete: () => { this.statsLoading = false; }
        });
    }

    /** Total syncs for a property row (used for sorting / health highlight). */
    propertyTotalSyncs(row: any): number {
        return (row?.propertySync ?? 0) + (row?.reservationSync ?? 0) + (row?.rateSync ?? 0) + (row?.rateLosSync ?? 0);
    }

    /** Total errors for a property row. */
    propertyTotalErrors(row: any): number {
        return (row?.propertyErr ?? 0) + (row?.reservationErr ?? 0) + (row?.rateErr ?? 0) + (row?.rateLosErr ?? 0);
    }

    // ─── Health Checks ────────────────────────────────────────────────────────

    loadHealth(): void {
        this.healthLoading = true;
        const req = (this.selectedProperties?.length === 1)
            ? { propertyId: this.selectedProperties[0].propertyId }
            : {};
        this.opsDashboardService.getHealthSummary(req).subscribe({
            next: (res: any) => {
                this.healthSummary = res?.data?.health;
                this.healthLastRefresh = new Date();
            },
            error: () => {
                this.messageService.add({ severity: 'warn', summary: 'Health Check', detail: 'Could not load health summary.' });
                this.healthLoading = false;
            },
            complete: () => { this.healthLoading = false; }
        });
    }

    openDrillDown(item: any): void {
        if (!item || item.count === 0) { return; }
        this.drillDownTitle      = item.label;
        this.drillDownDesc       = item.description;
        this.drillDownProperties = item.properties ?? [];
        this.drillDownVisible    = true;
    }

    healthSeverityIcon(severity: string): string {
        switch (severity) {
            case 'ERROR': return 'pi pi-times-circle';
            case 'WARN':  return 'pi pi-exclamation-triangle';
            case 'INFO':  return 'pi pi-info-circle';
            default:      return 'pi pi-check-circle';
        }
    }

    healthSeverityClass(severity: string): string {
        switch (severity) {
            case 'ERROR': return 'ym-hc-error';
            case 'WARN':  return 'ym-hc-warn';
            case 'INFO':  return 'ym-hc-info';
            default:      return 'ym-hc-ok';
        }
    }

    listingStatusLabel(status: string): string {
        return status ?? '—';
    }

    // ─── Task #86: ATL regen ─────────────────────────────────────────────────────

    triggerAtlAll(): void {
        this.atlAllLoading = true;
        const req: RateRegenRequest = { scope: 'ALL', generateRates: true, generateLos: true, triggerName: 'MANUAL' };
        this.rateRegenService.startRegenAtl(req).subscribe({
            next: (res: any) => {
                const jobId = res?.data?.scheduleJobLogId ?? res?.scheduleJobLogId;
                this.atlActiveJob = { id: jobId, box: 'ATL' };
                this.addToJobHistory(jobId, 'ATL', 'ALL');
                this.startAtlPoll(jobId);
                this.messageService.add({ severity: 'info', summary: 'ATL Regen Started', detail: `Job #${jobId} queued (ALL).` });
            },
            error: (err) => this.messageService.add({ severity: 'error', summary: 'ATL Regen Failed', detail: err?.error?.message ?? 'Check ATL-RPT connectivity.' }),
            complete: () => { this.atlAllLoading = false; }
        });
    }

    triggerAtlRange(): void {
        if (!this.atlRangeStart || !this.atlRangeEnd) {
            this.messageService.add({ severity: 'warn', summary: 'Range Required', detail: 'Enter both STR ID start and end.' });
            return;
        }
        this.atlRangeLoading = true;
        const req: RateRegenRequest = { scope: 'RANGE', strIdStart: this.atlRangeStart, strIdEnd: this.atlRangeEnd, generateRates: true, generateLos: true, triggerName: 'MANUAL' };
        this.rateRegenService.startRegenAtl(req).subscribe({
            next: (res: any) => {
                const jobId = res?.data?.scheduleJobLogId ?? res?.scheduleJobLogId;
                this.atlActiveJob = { id: jobId, box: 'ATL' };
                this.addToJobHistory(jobId, 'ATL', `RANGE[${this.atlRangeStart}-${this.atlRangeEnd}]`);
                this.startAtlPoll(jobId);
                this.messageService.add({ severity: 'info', summary: 'ATL Regen Started', detail: `Job #${jobId} queued (RANGE ${this.atlRangeStart}–${this.atlRangeEnd}).` });
            },
            error: (err) => this.messageService.add({ severity: 'error', summary: 'ATL Regen Failed', detail: err?.error?.message ?? 'Check ATL-RPT connectivity.' }),
            complete: () => { this.atlRangeLoading = false; }
        });
    }

    private startAtlPoll(jobId: number): void {
        this.atlPollSub?.unsubscribe();
        this.atlPollSub = interval(5000).pipe(
            switchMap(() => this.rateRegenService.getStatusAtl(jobId)),
            takeWhile((res: any) => {
                const log = res?.data?.parent ?? res?.parent;
                return log && !this.rateRegenService.isTerminal(log.jobStatus);
            }, true)
        ).subscribe({
            next: (res: any) => {
                const log = res?.data?.parent ?? res?.parent;
                if (this.atlActiveJob) { this.atlActiveJob.log = log; }
                this.updateJobHistoryLog(jobId, log);
            }
        });
    }

    // ─── Task #86: NYC regen ─────────────────────────────────────────────────────

    triggerNycAll(): void {
        this.nycAllLoading = true;
        const req: RateRegenRequest = { scope: 'ALL', generateRates: true, generateLos: true, triggerName: 'MANUAL' };
        this.rateRegenService.startRegenNyc(req).subscribe({
            next: (res: any) => {
                const jobId = res?.data?.scheduleJobLogId ?? res?.scheduleJobLogId;
                this.nycActiveJob = { id: jobId, box: 'NYC' };
                this.addToJobHistory(jobId, 'NYC', 'ALL');
                this.startNycPoll(jobId);
                this.messageService.add({ severity: 'info', summary: 'NYC Regen Started', detail: `Job #${jobId} queued (ALL).` });
            },
            error: (err) => this.messageService.add({ severity: 'error', summary: 'NYC Regen Failed', detail: err?.error?.message ?? 'Check NYC-RPT connectivity.' }),
            complete: () => { this.nycAllLoading = false; }
        });
    }

    triggerNycRange(): void {
        if (!this.nycRangeStart || !this.nycRangeEnd) {
            this.messageService.add({ severity: 'warn', summary: 'Range Required', detail: 'Enter both STR ID start and end.' });
            return;
        }
        this.nycRangeLoading = true;
        const req: RateRegenRequest = { scope: 'RANGE', strIdStart: this.nycRangeStart, strIdEnd: this.nycRangeEnd, generateRates: true, generateLos: true, triggerName: 'MANUAL' };
        this.rateRegenService.startRegenNyc(req).subscribe({
            next: (res: any) => {
                const jobId = res?.data?.scheduleJobLogId ?? res?.scheduleJobLogId;
                this.nycActiveJob = { id: jobId, box: 'NYC' };
                this.addToJobHistory(jobId, 'NYC', `RANGE[${this.nycRangeStart}-${this.nycRangeEnd}]`);
                this.startNycPoll(jobId);
                this.messageService.add({ severity: 'info', summary: 'NYC Regen Started', detail: `Job #${jobId} queued (RANGE ${this.nycRangeStart}–${this.nycRangeEnd}).` });
            },
            error: (err) => this.messageService.add({ severity: 'error', summary: 'NYC Regen Failed', detail: err?.error?.message ?? 'Check NYC-RPT connectivity.' }),
            complete: () => { this.nycRangeLoading = false; }
        });
    }

    private startNycPoll(jobId: number): void {
        this.nycPollSub?.unsubscribe();
        this.nycPollSub = interval(5000).pipe(
            switchMap(() => this.rateRegenService.getStatusNyc(jobId)),
            takeWhile((res: any) => {
                const log = res?.data?.parent ?? res?.parent;
                return log && !this.rateRegenService.isTerminal(log.jobStatus);
            }, true)
        ).subscribe({
            next: (res: any) => {
                const log = res?.data?.parent ?? res?.parent;
                if (this.nycActiveJob) { this.nycActiveJob.log = log; }
                this.updateJobHistoryLog(jobId, log);
            }
        });
    }

    // ─── Task #95: Job History ────────────────────────────────────────────────────

    private addToJobHistory(id: number, box: 'ATL' | 'NYC', scope: string): void {
        this.jobHistory.unshift({ id, box, scope });
        if (this.jobHistory.length > 50) { this.jobHistory.pop(); }
    }

    private updateJobHistoryLog(id: number, log: any): void {
        const entry = this.jobHistory.find(j => j.id === id);
        if (entry) { entry.log = log; }
    }

    jobStatusLabel(status: number): string  { return this.rateRegenService.statusLabel(status); }
    jobStatusSeverity(status: number): string { return this.rateRegenService.statusSeverity(status); }

    get jobProgressPercent(): (log: any) => number {
        return (log: any) => {
            if (!log || !log.totalItems || log.totalItems === 0) { return 0; }
            return Math.round((log.processedItems / log.totalItems) * 100);
        };
    }
}
